import { HttpClient, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MOMENT_DATE_FORMAT_DASH_REVERSED } from '@app/app.constants';
import { environment } from '@root/environments/environment';
import moment from 'moment';
import { map, Observable } from 'rxjs';
import { PricingCountryFilters } from '../store/pricing-country.state';
import {
  CountryBundlePrice,
  CountryBundlePriceApiModelData,
  CountryBundlePricePivot,
  CountryBundlePricePivotApiModelData,
  CountryPriceType,
  PricingCountry,
  PricingCountryApiModel,
  PricingCountryApiModelData,
  PricingCountryList,
} from '../types/pricing.types';
import { PricingCountryUtilsService } from './pricing-country-utils.service';

@Injectable({
  providedIn: 'root',
})
export class PricingCountryService {
  private http = inject(HttpClient);
  private pricingCountryUtilsService = inject(PricingCountryUtilsService);

  private readonly API_URL = `${environment.apiUrl}/nipt-invoicing`;

  public getPricingCountry(id: string): Observable<PricingCountry> {
    return this.http
      .get<PricingCountryApiModelData>(`${this.API_URL}/countries/${id}`)
      .pipe(
        map((model: PricingCountryApiModelData) =>
          this.pricingCountryUtilsService.apiModelToPricingCountry(model)
        )
      );
  }

  public getPricingCountriesList(
    filters: PricingCountryFilters
  ): Observable<PricingCountryList> {
    return this.http
      .get<PricingCountryApiModel | PricingCountryApiModelData[]>(
        `${this.API_URL}/countries`,
        {
          params:
            this.pricingCountryUtilsService.buildPricingCountryHttpParams(
              filters
            ),
        }
      )
      .pipe(
        map((model: PricingCountryApiModel | PricingCountryApiModelData[]) => {
          // Filtering and paging are done by the backend (`name`, `currency`,
          // `notes`, `page`, `limit` query params). Everything returned is
          // displayed as-is; a bare array is treated as a single page.
          if (Array.isArray(model)) {
            const data = model.map((country: PricingCountryApiModelData) =>
              this.pricingCountryUtilsService.apiModelToPricingCountry(country)
            );

            return {
              data,
              totalCount: data.length,
              hasMorePages: false,
              perPage: data.length,
              currentPage: filters.page || 1,
            };
          }

          return {
            data: model.data.map((country: PricingCountryApiModelData) =>
              this.pricingCountryUtilsService.apiModelToPricingCountry(country)
            ),
            totalCount: model.total_count,
            hasMorePages: model.has_more_pages,
            perPage: model.per_page,
            currentPage: model.current_page,
          };
        })
      );
  }

  public updatePricingCountry(
    countryId: string,
    body: { currency?: string; notes?: string }
  ): Observable<void> {
    return this.http.patch<void>(
      `${this.API_URL}/countries/${countryId}`,
      body
    );
  }

  public getPricingCountryBundles(
    countryId: string,
    priceType?: CountryPriceType
  ): Observable<CountryBundlePrice[]> {
    return this.http
      .get<CountryBundlePriceApiModelData[]>(
        `${this.API_URL}/countries/${countryId}/bundles`,
        {
          params: priceType ? { type: priceType } : {},
        }
      )
      .pipe(
        map((bundles: CountryBundlePriceApiModelData[]) =>
          bundles
            .map((bundle: CountryBundlePriceApiModelData) =>
              this.pricingCountryUtilsService.apiModelToPricingCountryBundle(
                bundle
              )
            )
            .filter((bundle) => !!bundle)
        )
      );
  }

  public addBundlePriceToPricingCountry(
    countryId: string,
    bundleId: string,
    price: number,
    priceType: CountryPriceType,
    validFrom: Date
  ): Observable<CountryBundlePricePivotApiModelData> {
    return this.http.post<CountryBundlePricePivotApiModelData>(
      `${this.API_URL}/countries/${countryId}/bundles/${bundleId}`,
      {
        price,
        type: priceType,
        // null for a first EUP; the backend then applies 1 January 1970
        valid_from: validFrom
          ? moment(validFrom).format(MOMENT_DATE_FORMAT_DASH_REVERSED)
          : null,
      }
    );
  }

  public updatePricingCountryBundlePrice(
    countryId: string,
    pivotId: string,
    price: number,
    validFrom?: Date
  ): Observable<{ price: number; valid_from: string }> {
    const body: { price: number; valid_from?: string } = { price };

    if (validFrom) {
      body.valid_from = moment(validFrom).format(
        MOMENT_DATE_FORMAT_DASH_REVERSED
      );
    }

    return this.http.put<{ price: number; valid_from: string }>(
      `${this.API_URL}/countries/${countryId}/bundles/${pivotId}`,
      body
    );
  }

  public deletePricingCountryBundlePrice(
    countryId: string,
    pivotId: string
  ): Observable<void> {
    return this.http.delete<void>(
      `${this.API_URL}/countries/${countryId}/bundles/${pivotId}`
    );
  }

  public getPricingCountryBundlePriceHistory(
    countryId: string,
    bundleId: string,
    priceType?: CountryPriceType
  ): Observable<CountryBundlePricePivot[]> {
    return this.http
      .get<CountryBundlePricePivotApiModelData[]>(
        `${this.API_URL}/countries/${countryId}/bundles/${bundleId}/history`,
        {
          params: priceType ? { type: priceType } : {},
        }
      )
      .pipe(
        map((history: CountryBundlePricePivotApiModelData[]) =>
          history.map((row: CountryBundlePricePivotApiModelData) =>
            this.pricingCountryUtilsService.apiModelToPricingCountryBundlePivot(
              row
            )
          )
        )
      );
  }

  public exportPricelist(
    filters: PricingCountryFilters
  ): Observable<{ blob: Blob; filename: string }> {
    return this.http
      .get(`${this.API_URL}/export/pricelist`, {
        responseType: 'blob',
        observe: 'response',
        params:
          this.pricingCountryUtilsService.buildPricingCountryHttpParams(
            filters
          ),
      })
      .pipe(
        map((response: HttpResponse<Blob>) => ({
          blob: response.body,
          filename: response.headers
            .get('Content-Disposition')
            ?.split('filename=')[1]
            .split(';')[0],
        }))
      );
  }
}
